import React, { useState, useRef, useEffect } from 'react';
import styles from '../../styles/CreativeEditor.module.css';
import cardStyles from '../../styles/CreativeCard.module.css';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

interface LabelSectionProps {
  labels: string[];
  availableLabels: string[];
  isReadOnly: boolean;
  onAddLabel: (label: string) => Promise<void>;
  onRemoveLabel: (label: string) => void;
}

const LabelSection: React.FC<LabelSectionProps> = ({
  labels,
  availableLabels,
  isReadOnly,
  onAddLabel,
  onRemoveLabel,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [newLabelInput, setNewLabelInput] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const handleAddCustomLabel = async (e: React.FormEvent) => {
    e.preventDefault();
    const label = newLabelInput.trim();
    if (!label) return;
    await onAddLabel(label);
    setNewLabelInput('');
  };

  const handleToggleLabel = async (label: string) => {
    if (labels.includes(label)) {
      onRemoveLabel(label);
    } else {
      await onAddLabel(label);
    }
  };

  return (
    <div className={styles.labelPillsContainer} ref={dropdownRef}>
      {labels.map((label) => (
        <span key={label} className={styles.labelPill}>
          <span className={styles.labelPillText}>{label}</span>
          {!isReadOnly && (
            <button
              className={styles.labelPillRemove}
              onClick={() => onRemoveLabel(label)}
              title={`Remove ${label}`}
            >
              <CloseIcon style={{ fontSize: 12 }} />
            </button>
          )}
        </span>
      ))}

      {!isReadOnly && (
        <button
          className={styles.addLabelTrigger}
          onClick={() => setIsDropdownOpen((prev) => !prev)}
          title="Manage labels"
        >
          <AddIcon style={{ fontSize: 14 }} />
          <span>Add label</span>
        </button>
      )}

      {isDropdownOpen && !isReadOnly && (
        <div className={cardStyles.cardLabelDropdown}>
          <ul className={cardStyles.cardLabelList}>
            {availableLabels.map((label) => (
              <li
                key={label}
                className={cardStyles.cardLabelItem}
                onClick={() => handleToggleLabel(label)}
              >
                <span>{label}</span>
                {labels.includes(label) && (
                  <CheckIcon style={{ fontSize: 16, color: 'var(--blue-color, #2E90FA)' }} />
                )}
              </li>
            ))}
          </ul>
          <form className={cardStyles.cardLabelAddForm} onSubmit={handleAddCustomLabel}>
            <AddIcon style={{ fontSize: 14, color: 'var(--text-light)' }} />
            <input
              type="text"
              className={cardStyles.cardLabelAddInput}
              placeholder="Add custom label"
              value={newLabelInput}
              onChange={(e) => setNewLabelInput(e.target.value)}
            />
          </form>
        </div>
      )}
    </div>
  );
};

export default LabelSection;
